/* Lectures — a full-screen swipe feed (one lecture per screen) of real,
   full-length lectures from the speakers' own YouTube channels (never YouTube
   Shorts). Each plays in 3-minute parts; when a part ends the next starts
   after a short countdown, or swipe for the next lecture. Any lecture can be
   saved (the bookmark) and found again under "Saved", which keeps working after
   the video has left the latest feed.

   ONE player for the whole feed. A browser only plays with sound after a tap,
   and that permission belongs to the player (the <video> inside the embed): a
   fresh player per part or lecture needs a fresh tap on iPhone. So there is a
   single embed, and each part or lecture is loaded into it with the embed's
   loadVideoById — one tap, on our own Start button, and everything after plays
   with sound. The player sits in the scroller (sticky), so a swipe over the
   video still changes lecture; it is hidden while the cards slide.

   The embed is driven by YouTube's postMessage protocol (no YouTube script is
   loaded into the app, which keeps the CSP tight). */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bookmark, BookmarkCheck, ChevronsDown, Loader2, Play, RotateCcw, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { PART_SECONDS, type Lecture } from '@shared/lectures';
import { usePlayer } from '@/features/player/audioEngine';
import { useRuqyah } from '@/features/adhkar/ruqyahPlayer';
import { alive, useProgress } from '@/lib/store';
import type { LectureMark } from '@/lib/types';
import { haptic } from '@/components/ui';

const EMBED = 'https://www.youtube-nocookie.com';
const COUNTDOWN = 3;
/* A browser may refuse to autoplay with sound. Once the player has spoken, if it still hasn't started playing
   after READY_WAIT_MS we show our Start button; if it hasn't even spoken after HARD_WAIT_MS, we show it anyway. */
const READY_WAIT_MS = 1800;
const HARD_WAIT_MS = 5000;
/* If playing still hasn't begun this long after a tap on Start, our cover steps aside so YouTube's own play button is reachable. */
const TAP_GRACE_MS = 2500;
const SETTLE_MS = 140; // the feed counts as still while no scroll event for this long
/* Stable fallback — a fresh {} in a store selector re-renders forever. */
const NO_MARKS: Record<string, LectureMark> = {};

async function loadLectures(): Promise<Lecture[]> {
  const r = await fetch('/api/lectures');
  if (!r.ok) throw new Error('Could not load lectures');
  return ((await r.json()) as { lectures: Lecture[] }).lectures;
}

const asLecture = (m: LectureMark): Lecture => ({ id: m.id, title: m.title, channel: m.channel, channelId: m.channelId, published: '' });
const thumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const range = (part: number) => ({ startSeconds: part * PART_SECONDS, endSeconds: (part + 1) * PART_SECONDS });

export function LecturesFeed() {
  const q = useQuery({ queryKey: ['lectures'], queryFn: loadLectures, staleTime: 30 * 60_000 });
  const marks = useProgress((s) => s.lectureMarks ?? NO_MARKS);
  const toggleMark = useProgress((s) => s.toggleLectureMark);
  const setMarkPart = useProgress((s) => s.setLectureMarkPart);
  const savedIds = useMemo(() => new Set(alive(marks).map((m) => m.id)), [marks]);
  const [mode, setMode] = useState<'latest' | 'saved'>('latest');
  /* Saved is a snapshot taken when you open it, so a lecture you un-save while watching stays
     (and can be saved again) until you leave. */
  const [snapshot, setSnapshot] = useState<Lecture[]>([]);
  const [muted, setMuted] = useState(false); // sound on from the start; the browser decides whether that can autoplay
  const [skip, setSkip] = useState<Set<string>>(() => new Set());
  const [gone, setGone] = useState<Set<string>>(() => new Set()); // saved videos YouTube no longer serves
  const feed = useMemo(() => (mode === 'saved' ? snapshot : (q.data ?? []).filter((l) => !skip.has(l.id))), [mode, snapshot, q.data, skip]);

  /* ── Which lecture is on screen ───────────────────────────────────────── */
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [scrolling, setScrolling] = useState(false);
  const [height, setHeight] = useState(0);
  const cur: Lecture | undefined = feed[Math.min(active, feed.length - 1)];
  const curIndex = cur ? feed.indexOf(cur) : -1;

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const measure = () => setHeight(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let t = 0;
    const onScroll = () => {
      setScrolling(true);
      clearTimeout(t);
      t = window.setTimeout(() => { setScrolling(false); setActive(Math.round(el.scrollTop / (el.clientHeight || 1))); }, SETTLE_MS);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(t); };
  }, []);

  const goTo = useCallback((i: number) => {
    const el = scroller.current;
    el?.scrollTo({ top: i * el.clientHeight, behavior: 'smooth' });
  }, []);

  const show = (m: 'latest' | 'saved') => {
    if (m === mode) return;
    haptic(6);
    if (m === 'saved') setSnapshot(alive(marks).sort((a, b) => b.createdAt - a.createdAt).map(asLecture));
    scroller.current?.scrollTo({ top: 0 });
    setActive(0);
    setMode(m);
  };

  // Only one thing plays at a time: entering the feed stops recitation and ruqyah audio.
  useEffect(() => {
    if (usePlayer.getState().status !== 'idle') usePlayer.getState().pause();
    if (useRuqyah.getState().status === 'playing') useRuqyah.getState().toggle();
  }, []);

  /* ── Parts ────────────────────────────────────────────────────────────── */
  const [partOf, setPartOf] = useState<Record<string, number>>({});
  const [durations, setDurations] = useState<Record<string, number>>({});
  const partsFor = (id: string) => (durations[id] ? Math.max(1, Math.ceil(durations[id] / PART_SECONDS)) : null);
  /* A saved lecture opens at the part you were on (kept on its bookmark, so it follows you to other devices). */
  const partFor = (id: string) => {
    const mark = marks[id];
    const p = partOf[id] ?? (mark && !mark.deleted ? mark.part : undefined) ?? 0;
    const n = partsFor(id);
    return n ? Math.min(p, n - 1) : p;
  };
  const setPart = (id: string, p: number) => { setPartOf((o) => ({ ...o, [id]: p })); setMarkPart(id, p); };
  const part = cur ? partFor(cur.id) : 0;
  const parts = cur ? partsFor(cur.id) : null;
  const hasMore = parts == null || part + 1 < parts;
  const hasNext = curIndex >= 0 && curIndex + 1 < feed.length;

  /* ── The one player ───────────────────────────────────────────────────── */
  const frame = useRef<HTMLIFrameElement>(null);
  const [boot, setBoot] = useState<{ id: string; part: number; muted: boolean } | null>(null); // what the embed's first URL loads
  const loaded = useRef<string | null>(null); // `${id}:${part}` the player was last asked to play
  const [ready, setReady] = useState(false); // the player has spoken (it is up)
  const [started, setStarted] = useState(false); // … and is playing (or buffering to) the current part
  const [ended, setEnded] = useState(false);
  /* Has the part now loading actually begun? Until it has, an "ended" from the player is the part that
     just finished (it reports that again as the next one loads) — not this one: ignore it, so moving on
     to the next part never flashes "Stay here". */
  const begun = useRef(false);
  const [count, setCount] = useState(COUNTDOWN);
  const [needsTap, setNeedsTap] = useState(false);
  const [tapped, setTapped] = useState(false);
  const [coverOff, setCoverOff] = useState(false);
  const mutedNow = useRef(muted);
  mutedNow.current = muted;
  const modeNow = useRef(mode);
  modeNow.current = mode;

  const send = useCallback((func: string, args: unknown[] = []) => {
    frame.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), EMBED);
  }, []);

  // The embed is created once, for whichever lecture is first on screen. Everything after goes through loadVideoById.
  useEffect(() => {
    if (boot || !cur) return;
    loaded.current = `${cur.id}:${part}`;
    setBoot({ id: cur.id, part, muted: mutedNow.current });
  }, [boot, cur, part]);

  const want = cur ? `${cur.id}:${part}` : null;
  // The lecture or part on screen changed (and the cards have stopped sliding): load it into the player.
  useEffect(() => {
    if (!cur || !want || !ready || scrolling || loaded.current === want) return;
    loaded.current = want;
    setStarted(false); setEnded(false); setCount(COUNTDOWN); setNeedsTap(false); setTapped(false); setCoverOff(false);
    begun.current = false;
    send('loadVideoById', [{ videoId: cur.id, ...range(part) }]);
    send(mutedNow.current ? 'mute' : 'unMute');
  }, [want, cur, part, ready, scrolling, send]);

  // Listen to the player: duration, playing, ended, errors.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== EMBED || e.source !== frame.current?.contentWindow || typeof e.data !== 'string') return;
      let m: { event?: string; info?: { duration?: number; playerState?: number } | number };
      try { m = JSON.parse(e.data); } catch { return; }
      setReady(true);
      const id = loaded.current?.split(':')[0];
      const state = m.event === 'infoDelivery' && typeof m.info === 'object' ? m.info.playerState : m.event === 'onStateChange' && typeof m.info === 'number' ? m.info : undefined;
      if (m.event === 'infoDelivery' && typeof m.info === 'object' && m.info.duration && m.info.duration > 0 && id) {
        const d = m.info.duration;
        setDurations((o) => (o[id] === d ? o : { ...o, [id]: d }));
      }
      if (state === 1) begun.current = true;                // playing: this part is under way
      if (state === 0 && begun.current) setEnded(true);
      if (state === 1 || state === 3) setStarted(true); // playing / buffering
      // 100: removed/private · 101/150: the channel doesn't allow embedding. The latest feed just drops it;
      // a saved one stays (marked), so it can be removed.
      if (m.event === 'onError' && (m.info === 100 || m.info === 101 || m.info === 150) && id) {
        if (modeNow.current === 'saved') setGone((s) => new Set(s).add(id)); else setSkip((s) => new Set(s).add(id));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  useEffect(() => { if (ready) send(muted ? 'mute' : 'unMute'); }, [muted, ready, send]);

  // Autoplay with sound refused (or just slow): after a moment, offer our own Start button.
  useEffect(() => {
    if (started || !cur || gone.has(cur.id)) { setNeedsTap(false); return; }
    const soon = ready ? setTimeout(() => setNeedsTap(true), READY_WAIT_MS) : undefined;
    const late = setTimeout(() => setNeedsTap(true), HARD_WAIT_MS);
    return () => { clearTimeout(soon); clearTimeout(late); };
  }, [started, ready, want, cur, gone]);

  // We were tapped but the player still won't start (some browsers insist on a tap inside the video itself): step aside.
  useEffect(() => {
    if (!tapped || started) return;
    const t = setTimeout(() => setCoverOff(true), TAP_GRACE_MS);
    return () => clearTimeout(t);
  }, [tapped, started]);

  /** Our Start button: one tap, in our own document — it unmutes and plays, and the browser counts it as the user's. */
  const start = () => {
    haptic(12);
    setMuted(false);
    send('unMute');
    send('playVideo');
    setTapped(true);
  };

  // Once a lecture is on screen, its part is yours: a bookmark updated meanwhile (another device syncing) doesn't move it.
  // (Only once saved data has loaded — before that, a bookmark's part isn't known yet.)
  const hydrated = useProgress.persist.hasHydrated();
  useEffect(() => {
    if (cur && hydrated) setPartOf((o) => (cur.id in o ? o : { ...o, [cur.id]: part }));
  }, [cur?.id, hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  /* A saved lecture's part was watched to the end: its bookmark moves on to the next part straight away,
     so tapping "Stay here" (or closing the app during the countdown) doesn't mean watching it again. */
  useEffect(() => {
    if (!ended || !cur || !hasMore) return;
    // Pin what's on screen first: from here the card follows what you do, not the bookmark.
    setPartOf((o) => (cur.id in o ? o : { ...o, [cur.id]: part }));
    setMarkPart(cur.id, part + 1);
  }, [ended]); // eslint-disable-line react-hooks/exhaustive-deps

  // Part finished: count down, then play the next part (or move to the next lecture).
  useEffect(() => {
    if (!ended || !cur) return;
    if (count <= 0) {
      if (hasMore) { begun.current = false; setPart(cur.id, part + 1); setEnded(false); setCount(COUNTDOWN); } else if (hasNext) goTo(curIndex + 1);
      return;
    }
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [ended, count, hasMore, hasNext, cur, part, curIndex, goTo]);

  /* ── Render ───────────────────────────────────────────────────────────── */
  const tab = (m: 'latest' | 'saved', label: string) => (
    <button type="button" role="tab" aria-selected={mode === m} onClick={() => show(m)}
      className={`press rounded-full px-3.5 py-1.5 text-[14px] font-semibold ${mode === m ? 'bg-white text-black' : 'text-white/85'}`}>{label}</button>
  );
  const playerShown = !!cur && !scrolling && !gone.has(cur.id);
  const covered = playerShown && !started && !coverOff;
  const empty = !feed.length;

  return (
    // A tab like the others: the feed stops above the tab bar instead of covering it.
    <div className="fixed inset-x-0 top-0 z-30 bg-black text-white" style={{ bottom: 0 }}>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between px-3" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <span className="w-10" aria-hidden />
        <div role="tablist" aria-label="Lectures" className="pointer-events-auto flex gap-1 rounded-full bg-black/45 p-1 backdrop-blur-md">
          {tab('latest', 'Latest')}
          {tab('saved', savedIds.size ? `Saved · ${savedIds.size}` : 'Saved')}
        </div>
        <button type="button" aria-label={muted ? 'Turn sound on' : 'Mute'} className="press pointer-events-auto grid h-10 w-10 place-items-center rounded-full bg-black/40 backdrop-blur-md" onClick={() => { haptic(); setMuted(!muted); }}>
          {muted ? <VolumeX size={19} /> : <Volume2 size={19} />}
        </button>
      </div>

      <div ref={scroller} className="no-scrollbar h-full snap-y snap-mandatory overflow-y-scroll overscroll-contain">
        {/* The player: pinned to the top of the scroller (zero height, so it takes no room), over whichever card is on screen. */}
        <div className="sticky top-0 z-[5] h-0">
          <div className="pointer-events-none absolute inset-x-0 top-0 grid place-items-center" style={{ height }}>
            {boot && (
              <div className={`relative aspect-video w-full transition-opacity duration-150 ${playerShown ? 'pointer-events-auto opacity-100' : 'opacity-0'}`}>
                <iframe ref={frame} title={cur?.title ?? 'Lecture'} className="absolute inset-0 h-full w-full"
                  src={`${EMBED}/embed/${boot.id}?enablejsapi=1&playsinline=1&autoplay=1&mute=${boot.muted ? 1 : 0}&rel=0&modestbranding=1&iv_load_policy=3&origin=${encodeURIComponent(location.origin)}&start=${boot.part * PART_SECONDS}&end=${(boot.part + 1) * PART_SECONDS}`}
                  onLoad={() => frame.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: boot.id, channel: 'widget' }), EMBED)}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
                {covered && (
                  // Our own cover: hides YouTube's start button, and is where the tap that unlocks sound happens.
                  <div className="absolute inset-0 z-10 grid place-items-center bg-black" data-testid="lecture-cover">
                    <img src={thumb(cur!.id)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
                    {needsTap ? (
                      <button type="button" aria-label="Play lecture" onClick={start}
                        className="press relative grid h-[72px] w-[72px] place-items-center rounded-full bg-white text-black shadow-[0_8px_30px_rgba(0,0,0,.5)]">
                        <Play size={32} className="ml-1 fill-current" />
                      </button>
                    ) : <Loader2 className="relative animate-spin text-white/80" aria-label="Loading" />}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {feed.map((l, i) => (
          <section key={l.id} data-index={i} className="relative h-full w-full snap-start snap-always overflow-hidden" aria-label={l.title}>
            <LectureCard lecture={l} on={cur?.id === l.id} part={partFor(l.id)} parts={partsFor(l.id)}
              hasMore={cur?.id === l.id ? hasMore : (partsFor(l.id) ?? Infinity) > partFor(l.id) + 1} hasNext={i + 1 < feed.length}
              saved={savedIds.has(l.id)} unavailable={gone.has(l.id)}
              ended={ended && cur?.id === l.id} count={count}
              onSave={() => { haptic(); toggleMark({ ...l, part: partFor(l.id) }); }}
              onPart={(p) => { haptic(6); begun.current = false; setPart(l.id, p); setEnded(false); setCount(COUNTDOWN); }}
              onNext={() => goTo(i + 1)}
              onStay={() => { setEnded(false); setCount(COUNTDOWN); send('pauseVideo'); }} />
          </section>
        ))}
      </div>

      {/* States that stand in for the feed. */}
      {mode === 'latest' && q.isPending && <div className="absolute inset-0 grid place-items-center"><Loader2 className="animate-spin" /></div>}
      {mode === 'latest' && !q.isPending && empty && (
        <div className="absolute inset-0 grid place-items-center px-8 text-center">
          <div><p className="text-white/80">Couldn’t load lectures — check your connection.</p>
            <button type="button" className="press mt-4 rounded-full bg-white/15 px-5 py-2.5 font-semibold" onClick={() => void q.refetch()}>Try again</button></div>
        </div>
      )}
      {mode === 'saved' && empty && (
        <div className="absolute inset-0 grid place-items-center px-8 text-center">
          <div>
            <Bookmark size={36} className="mx-auto text-white/50" />
            <p className="mt-3 text-[17px] font-semibold">Nothing saved yet</p>
            <p className="mt-1 text-white/70">Tap the bookmark on a lecture to keep it here.</p>
            <button type="button" className="press mt-4 rounded-full bg-white/15 px-5 py-2.5 font-semibold" onClick={() => show('latest')}>Browse lectures</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** One lecture's screen: its picture, its details and its controls. The video itself is the feed's single player, drawn over this. */
function LectureCard({ lecture: l, on, part, parts, hasMore, hasNext, saved, unavailable, ended, count, onSave, onPart, onNext, onStay }: {
  lecture: Lecture; on: boolean; part: number; parts: number | null; hasMore: boolean; hasNext: boolean; saved: boolean; unavailable: boolean;
  ended: boolean; count: number; onSave: () => void; onPart: (p: number) => void; onNext: () => void; onStay: () => void;
}) {
  return (
    <>
      <div className="absolute inset-0 grid place-items-center">
        <img src={thumb(l.id)} alt="" loading="lazy" className="aspect-video w-full object-cover opacity-70" />
      </div>

      {/* Info over the bottom of the card; leaves the middle free for the player's own controls. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-4 pt-16" style={{ paddingBottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 20px)' }}>
        <div className="text-[13px] font-semibold text-[#f3d27a]">{l.channel}</div>
        <div className="line-clamp-2 text-[16px] font-semibold leading-snug">{l.title}</div>
        <div className="pointer-events-auto mt-3 flex flex-wrap items-center gap-2">
          <button type="button" aria-label={saved ? 'Remove from saved' : 'Save lecture'} aria-pressed={saved} onClick={onSave}
            className={`press grid h-9 w-9 place-items-center rounded-full ${saved ? 'bg-[#f3d27a] text-black' : 'bg-white/15'}`}>
            {saved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
          </button>
          <span className="rounded-full bg-white/15 px-3 py-1.5 text-[13px] font-semibold tabular-nums">Part {part + 1}{parts ? ` of ${parts}` : ''} · 3 min</span>
          {part > 0 && <button type="button" aria-label="Previous part" onClick={() => onPart(part - 1)} className="press grid h-8 w-8 place-items-center rounded-full bg-white/15"><RotateCcw size={15} /></button>}
          {hasMore && <button type="button" onClick={() => onPart(part + 1)} className="press flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[13px] font-semibold"><SkipForward size={15} /> Next part</button>}
          {hasNext && <button type="button" onClick={onNext} className="press flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[13px] font-semibold"><ChevronsDown size={15} /> Next lecture</button>}
        </div>
      </div>

      {unavailable && (
        <div className="absolute inset-x-0 top-1/2 z-20 -translate-y-1/2 px-8 text-center">
          <p className="rounded-2xl bg-black/75 px-4 py-3 text-[15px] text-white/90">This video is no longer available on YouTube. You can remove it from your saved lectures.</p>
        </div>
      )}

      {ended && on && (
        <div className="absolute inset-0 z-20 grid place-items-center bg-black/70 px-8 text-center">
          <div>
            <p className="text-[15px] text-white/80">{hasMore ? `Part ${part + 2}${parts ? ` of ${parts}` : ''} in ${count}…` : hasNext ? `Next lecture in ${count}…` : 'You’re all caught up.'}</p>
            <div className="mt-4 flex justify-center gap-3">
              {hasMore && <button type="button" className="press flex items-center gap-1.5 rounded-full bg-white px-4 py-2.5 font-semibold text-black" onClick={() => onPart(part + 1)}><Play size={16} /> Continue</button>}
              <button type="button" className="press rounded-full bg-white/15 px-4 py-2.5 font-semibold" onClick={onStay}>Stay here</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
